<template>
  <!-- 桌面端：保持原生 el-table 体验，几乎零侵入 -->
  <el-table
    v-if="!isMobile && !forceMobile"
    ref="tableRef"
    v-bind="$attrs"
    :data="data"
    :empty-text="emptyText"
    @selection-change="$emit('selection-change', $event)"
  >
    <slot />
  </el-table>

  <!-- 移动端：把表格数据降级成卡片列表 -->
  <div v-else class="responsive-cards" v-loading="$attrs.loading">
    <template v-if="data && data.length">
      <div
        v-for="(row, index) in data"
        :key="getRowKey(row, index)"
        class="responsive-card"
        :class="{
          'is-clickable': !!rowClick && !mobileExpandable,
          'is-mobile-expandable': mobileExpandable
        }"
        @click="!mobileExpandable && handleRowClick(row)"
      >
        <template v-if="mobileExpandable && primaryField">
          <!-- 紧凑行：主列 + 操作同一行，详情可展开 -->
          <div class="card-compact-header">
            <div
              class="card-title-inline"
              role="button"
              tabindex="0"
              :aria-expanded="isRowExpanded(row, index)"
              aria-label="展开或收起详情"
              @click="toggleRowExpanded(row, index)"
              @keydown.enter.prevent="toggleRowExpanded(row, index)"
              @keydown.space.prevent="toggleRowExpanded(row, index)"
            >
              <slot
                :name="`cell-${primaryField}`"
                :row="row"
                :value="row[primaryField]"
                :index="index"
              >
                {{ row[primaryField] }}
              </slot>
            </div>
            <div v-if="$slots.actions" class="card-actions-inline" @click.stop>
              <slot name="actions" :row="row" :index="index" />
            </div>
          </div>

          <div v-show="isRowExpanded(row, index)" class="card-body card-body-expandable">
            <div v-if="$slots['mobile-expanded-top']" class="card-expanded-top">
              <slot name="mobile-expanded-top" :row="row" :index="index" />
            </div>
            <div
              v-for="field in visibleFields"
              :key="field.prop"
              class="card-row"
            >
              <span class="card-label">{{ field.label }}</span>
              <span class="card-value">
                <slot
                  :name="`cell-${field.prop}`"
                  :row="row"
                  :value="row[field.prop]"
                  :index="index"
                >
                  {{ formatValue(row[field.prop]) }}
                </slot>
              </span>
            </div>
          </div>
        </template>

        <template v-else>
          <!-- 标题区：使用 primary 列作为卡片标题 -->
          <div v-if="primaryField" class="card-title">
            <slot
              :name="`cell-${primaryField}`"
              :row="row"
              :value="row[primaryField]"
              :index="index"
            >
              {{ row[primaryField] }}
            </slot>
          </div>

          <!-- 字段列表 -->
          <div class="card-body">
            <div
              v-for="field in visibleFields"
              :key="field.prop"
              class="card-row"
            >
              <span class="card-label">{{ field.label }}</span>
              <span class="card-value">
                <slot
                  :name="`cell-${field.prop}`"
                  :row="row"
                  :value="row[field.prop]"
                  :index="index"
                >
                  {{ formatValue(row[field.prop]) }}
                </slot>
              </span>
            </div>
          </div>

          <!-- 操作区：允许调用方通过 actions 插槽自定义 -->
          <div v-if="$slots.actions" class="card-actions" @click.stop>
            <slot name="actions" :row="row" :index="index" />
          </div>
        </template>
      </div>
    </template>

    <el-empty v-else :description="emptyText" />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useResponsive } from '@/composables/useResponsive'

/**
 * ResponsiveTable：表格/卡片双形态组件
 *
 * 用法示例：
 *   <ResponsiveTable
 *     :data="studentList"
 *     :fields="[
 *       { prop: 'name', label: '姓名', primary: true },
 *       { prop: 'grade', label: '年级' },
 *       { prop: 'parent_phone', label: '家长电话' }
 *     ]"
 *     row-key="id"
 *     @row-click="openDetail"
 *   >
 *     <!-- 桌面端照常使用 el-table-column -->
 *     <el-table-column prop="name" label="姓名" />
 *     <el-table-column prop="grade" label="年级" />
 *     <el-table-column prop="parent_phone" label="家长电话" />
 *     <!-- 移动端单元格自定义（可选） -->
 *     <template #cell-name="{ row }">
 *       <el-link>{{ row.name }}</el-link>
 *     </template>
 *     <template #actions="{ row }">
 *       <el-button size="small" @click="edit(row)">编辑</el-button>
 *     </template>
 *   </ResponsiveTable>
 *
 * 设计原则：
 * - 桌面端完全透传给 el-table，不改变现有行为；
 * - 移动端只读 fields 配置生成卡片，列太多时可通过 hideOnMobile 隐藏次要列；
 * - 不重写单元格渲染逻辑，复杂单元格让调用方通过具名插槽接管。
 */

defineOptions({ inheritAttrs: false })

const props = defineProps({
  data: {
    type: Array,
    default: () => []
  },
  // 移动端卡片展示所用字段配置
  // 每项：{ prop, label, primary?: boolean, hideOnMobile?: boolean }
  fields: {
    type: Array,
    default: () => []
  },
  rowKey: {
    type: [String, Function],
    default: 'id'
  },
  emptyText: {
    type: String,
    default: '暂无数据'
  },
  // 点击整张卡片的回调（移动端专用，桌面端请用 el-table 的 row-click 事件）
  rowClick: {
    type: Function,
    default: null
  },
  // 移动端：首行仅展示主列与操作，点击展开按钮后展示其余字段
  mobileExpandable: {
    type: Boolean,
    default: false
  },
  forceMobile: {
    type: Boolean,
    default: false
  }
})

defineEmits(['selection-change'])

const { isMobile } = useResponsive()
const tableRef = ref(null)
const expandedKeySet = ref(new Set())

const primaryField = computed(() => {
  const primary = props.fields.find((f) => f.primary)
  return primary ? primary.prop : null
})

const visibleFields = computed(() =>
  props.fields.filter((f) => !f.primary && !f.hideOnMobile)
)

function getRowKey(row, index) {
  if (typeof props.rowKey === 'function') return props.rowKey(row)
  return row[props.rowKey] ?? index
}

const isRowExpanded = (row, index) =>
  expandedKeySet.value.has(getRowKey(row, index))

const toggleRowExpanded = (row, index) => {
  const key = getRowKey(row, index)
  const next = new Set(expandedKeySet.value)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  expandedKeySet.value = next
}

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '-'
  return value
}

const handleRowClick = (row) => {
  if (props.rowClick) props.rowClick(row)
}

// 暴露 el-table 的 ref 给父组件（桌面端的 clearSelection 等方法）
defineExpose({
  tableRef
})
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.responsive-cards {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0;
}

.responsive-card {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 12px;
  padding: 11px 12px !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;

  &.is-clickable {
    cursor: pointer;

    &:active {
      transform: scale(0.98);
      box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
    }
  }
}

.card-title {
  // 让标题区可以横向放下次要徽章（如状态 tag），调用方通过 cell-<primary> 插槽自定义
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px dashed rgba(0, 0, 0, 0.06);

  // 标题内的链接/文本溢出截断，避免挤压右侧徽章
  > * {
    min-width: 0;
  }
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  line-height: 1.6;
}

.card-label {
  color: #909399;
  flex-shrink: 0;
  min-width: 48px;
}

.card-value {
  color: #303133;
  text-align: right;
  word-break: break-all;
  flex: 1;
}

.card-actions {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed rgba(0, 0, 0, 0.06);
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.responsive-card.is-mobile-expandable {
  padding: 12px 14px !important;
}

.card-compact-header {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.card-title-inline {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  cursor: pointer;
  border-radius: 8px;
  outline: none;

  &:focus-visible {
    box-shadow: 0 0 0 2px var(--el-color-primary-light-5);
  }

  &:active {
    opacity: 0.85;
  }
}

.card-expanded-top {
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px dashed rgba(0, 0, 0, 0.06);
}

.card-actions-inline {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.card-body-expandable {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed rgba(0, 0, 0, 0.06);
}
</style>
